import { useId } from 'react';

/**
 * The Raad Track brand mark — two forward arrows (a bright LEAD arrow with a
 * darker FOLLOW arrow behind it), from Mahad's FINAL pack (28 Oct 2026, master at
 * brand/RaadTrack-final-pack/8-svg-vector/mark-for-{light,dark}-bg.svg). The paths
 * are copied VERBATIM from the pack (never redrawn).
 *
 * The mark is THEME-AWARE: on a light ground the arrows are brand blue + light
 * blue; on a dark ground the lead goes white and the follow a mid blue. That flip
 * is done with CSS custom properties (`--raad-logo-lead` / `--raad-logo-follow`)
 * that the cascade re-points on a `data-theme` change — no JS, no re-render. The
 * mark deliberately sits OUTSIDE the semantic palette (it is brand artwork, not UI
 * chrome) but it is still a token so the two grounds stay correct. The app favicon
 * (`public/favicon.svg`) and the PWA icons are the SAME arrows on a fixed white
 * tile (they do not flip) and live as their own files.
 *
 * `wordmark` renders the full lockup — the mark followed by "Raad Track" in Plus
 * Jakarta Sans ("Raad" 800, "Track" 500), each word its own theme-aware colour.
 * Without it (the default, and every existing call site) the component is the
 * square mark alone, byte-compatible with before.
 *
 * Inline SVG on purpose: it paints with the JS bundle already loading, one fewer
 * round trip on a Mogadishu 3G link than an <img src> would cost.
 */
const ARROW = 'M45 0 L90 100 Q45 72 0 100 Z';

export function RaadLogo({
  size = 40,
  title,
  wordmark = false,
}: {
  size?: number;
  title?: string;
  wordmark?: boolean;
}) {
  // A stable-per-instance mask id so multiple marks on one page don't clash
  // (useId, not size: two same-size marks would otherwise share a DOM id).
  const mid = useId();
  const labelled = title ? Boolean(title) : false;
  const mark = (
    <svg
      width={size}
      height={size}
      viewBox="14 16 73 66"
      role={labelled && !wordmark ? 'img' : 'presentation'}
      aria-label={!wordmark ? title : undefined}
      aria-hidden={wordmark || !title ? true : undefined}
    >
      <mask id={mid} maskUnits="userSpaceOnUse" x="0" y="0" width="100" height="100">
        <rect width="100" height="100" fill="white" />
        <g transform="translate(40,18) scale(0.5)">
          <path d={ARROW} fill="black" stroke="black" strokeWidth="11" strokeLinejoin="round" />
        </g>
      </mask>
      <g mask={`url(#${mid})`}>
        <g transform="translate(16,38) scale(0.42)">
          <path d={ARROW} fill="var(--raad-logo-follow)" />
        </g>
      </g>
      <g transform="translate(40,18) scale(0.5)">
        <path d={ARROW} fill="var(--raad-logo-lead)" />
      </g>
    </svg>
  );

  if (!wordmark) return mark;

  // The lockup: mark + "Raad Track". "Raad Track" is the product name — a brand
  // mark, identical in every language, so it is rendered as artwork rather than a
  // translated string.
  return (
    <span
      className="inline-flex items-center gap-2.5 font-display leading-none"
      role={title ? 'img' : undefined}
      aria-label={title}
    >
      {mark}
      <span aria-hidden="true" style={{ fontSize: Math.round(size * 0.52) }}>
        <span style={{ fontWeight: 800, color: 'var(--raad-wordmark-raad)' }}>Raad</span>
        <span style={{ fontWeight: 500, color: 'var(--raad-wordmark-track)' }}> Track</span>
      </span>
    </span>
  );
}
